import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Heading,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Button,
    useToast,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    VStack,
} from '@chakra-ui/react';
import getCSRFtoken from '../utils/getCSRFtoken';
import { spacing, buttonStyles } from '../theme/designSystem';
import fetchWithAuth from '../utils/fetchWithAuth';

function CreatePoll() {
    const csrfToken = getCSRFtoken();
    const navigate = useNavigate();
    const toast = useToast();
    const [pollData, setPollData] = useState({
        title: '',
        description: '',
        questions: []
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validation
        if (!pollData.title.trim()) {
            toast({
                title: 'Validation Error',
                description: 'Poll title is required.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        if (pollData.title.trim().length < 3) {
            toast({
                title: 'Validation Error',
                description: 'Poll title must be at least 3 characters.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetchWithAuth('/api/polls/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(pollData),
            });

            if (response.ok) {
                const data = await response.json();

                toast({
                    title: 'Poll Created',
                    description: `"${data.title}" has been created successfully.`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });

                // Reset form and close
                setPollData({
                    title: '',
                    description: '',
                    questions: []
                });
                // Navigate using React Router instead of hard redirect
                navigate(`/manage-questions/${data.id}`);
            } else {
                const errorData = await response.json().catch(() => ({}));
                toast({
                    title: 'Error Creating Poll',
                    description: errorData.error || errorData.detail || response.statusText || 'An unexpected error occurred.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Network Error',
                description: 'Could not connect to the server. Please check your connection and try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box py={8}>
            <Box maxWidth="800px" margin="0 auto">
                <Accordion allowToggle>
                    <AccordionItem
                        border="1px solid"
                        borderColor="border.default"
                        borderRadius="md"
                        bg="white"
                    >
                        <AccordionButton variant="section">
                            <Box flex="1" textAlign="left">
                                <Heading as="h3" size="md">
                                    Create a new poll
                                </Heading>
                            </Box>
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel variant="section" pb={spacing.group}>
                            <form onSubmit={handleSubmit}>
                                <VStack spacing={spacing.item} align="stretch">
                                    <FormControl id="title">
                                        <FormLabel>Title</FormLabel>
                                        <Input
                                            type="text"
                                            value={pollData.title}
                                            onChange={(e) => setPollData(prevData => ({ ...prevData, title: e.target.value }))}
                                            placeholder="Enter poll title"
                                        />
                                    </FormControl>
                                    <FormControl id="description">
                                        <FormLabel>Description</FormLabel>
                                        <Textarea
                                            value={pollData.description}
                                            onChange={(e) => setPollData(prevData => ({ ...prevData, description: e.target.value }))}
                                            placeholder="Enter poll description"
                                        />
                                    </FormControl>
                                    <Button
                                        type="submit"
                                        {...buttonStyles.primary}
                                        width="full"
                                        mt={spacing.item}
                                        isLoading={isSubmitting}
                                        loadingText="Creating Poll..."
                                        isDisabled={isSubmitting}
                                    >
                                        Create Poll
                                    </Button>
                                </VStack>
                            </form>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            </Box>
        </Box>
    );
}

export default CreatePoll;